/**
 * Index Template - Main JavaScript
 * Handles index-specific functionality including hero section interactions
 */

class IndexHero {
  constructor() {
    this.sections = document.querySelectorAll('.index-hero-section');
    this.init();
  }

  init() {
    this.sections.forEach(section => {
      this.initVideoContent(section);
      this.initImageHovers(section);
      this.initResponsiveText(section);
      this.initAccessibility(section);
    });
  }

  /**
   * Initialize video content functionality
   */
  initVideoContent(section) {
    const videoWrapper = section.querySelector('.index-hero__video-wrapper');
    const video = section.querySelector('video');
    
    if (!video || !videoWrapper) return;

    // Lazy load video on intersection
    this.initVideoLazyLoad(video);
    
    // Handle video errors
    video.addEventListener('error', () => {
      this.showVideoFallback(videoWrapper);
    });

    // Optimize video playback
    video.addEventListener('loadedmetadata', () => {
      // Ensure video maintains aspect ratio
      this.maintainVideoAspectRatio(video, videoWrapper);
    });

    // Handle mobile video optimization
    if (this.isMobile()) {
      this.optimizeMobileVideo(video);
    }
  }

  /**
   * Lazy load video when in viewport
   */
  initVideoLazyLoad(video) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const videoElement = entry.target;
          if (videoElement.dataset.src) {
            videoElement.src = videoElement.dataset.src;
            videoElement.load();
          }
          observer.unobserve(videoElement);
        }
      });
    }, {
      rootMargin: '50px'
    });

    if (video.dataset.src) {
      observer.observe(video);
    }
  }

  /**
   * Show video fallback on error
   */
  showVideoFallback(wrapper) {
    const fallback = document.createElement('div');
    fallback.className = 'index-hero__video-fallback';
    fallback.innerHTML = `
      <div class="w-full h-full bg-gray-800 flex items-center justify-center">
        <div class="text-center text-gray-400">
          <svg class="w-16 h-16 mx-auto mb-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z"/>
          </svg>
          <p>Video unavailable</p>
        </div>
      </div>
    `;
    
    wrapper.innerHTML = '';
    wrapper.appendChild(fallback);
  }

  /**
   * Maintain video aspect ratio
   */
  maintainVideoAspectRatio(video, wrapper) {
    const updateAspectRatio = () => {
      const aspectRatio = video.videoWidth / video.videoHeight;
      wrapper.style.aspectRatio = aspectRatio;
    };

    if (video.videoWidth && video.videoHeight) {
      updateAspectRatio();
    } else {
      video.addEventListener('loadedmetadata', updateAspectRatio);
    }
  }

  /**
   * Optimize video for mobile
   */
  optimizeMobileVideo(video) {
    // Reduce video quality on mobile for better performance
    video.setAttribute('playsinline', '');
    video.setAttribute('muted', '');
    
    // Check if video already has an observer to prevent conflicts
    if (video.hasAttribute('data-video-observer-attached')) {
      console.log('Video already has observer attached, skipping mobile optimization observer');
      return;
    }
    
    // AUTO PAUSE DISABLED - Videos will continue playing even when out of view
    // const observer = new IntersectionObserver((entries) => {
    //   entries.forEach(entry => {
    //     const videoElement = entry.target;
    //     if (entry.isIntersecting) {
    //       videoElement.play().catch(() => {
    //         // Handle autoplay restrictions
    //         console.log('Autoplay prevented');
    //       });
    //     } else {
    //       videoElement.pause();
    //     }
    //   });
    // }, {
    //   threshold: 0.5
    // });

    // observer.observe(video);
    // video.setAttribute('data-video-observer-attached', 'true');
  }

  /**
   * Initialize image hover effects
   */
  initImageHovers(section) {
    const imageBlocks = section.querySelectorAll('.index-hero__image-block');
    
    imageBlocks.forEach(block => {
      const img = block.querySelector('img');
      const text = block.querySelector('h3');
      
      if (!img || !text) return;

      // Add smooth hover transitions
      block.addEventListener('mouseenter', () => {
        this.animateImageHover(img, text, true);
      });

      block.addEventListener('mouseleave', () => {
        this.animateImageHover(img, text, false);
      });

      // Add touch support for mobile
      block.addEventListener('touchstart', () => {
        this.animateImageHover(img, text, true);
      });

      block.addEventListener('touchend', () => {
        setTimeout(() => {
          this.animateImageHover(img, text, false);
        }, 2000);
      });
    });
  }

  /**
   * Animate image hover effects
   */
  animateImageHover(img, text, isHover) {
    if (isHover) {
      img.style.transform = 'scale(1.05)';
      text.style.transform = 'translateY(-5px)';
      text.style.textShadow = '2px 2px 8px rgba(0, 0, 0, 0.8)';
    } else {
      img.style.transform = 'scale(1)';
      text.style.transform = 'translateY(0)';
      text.style.textShadow = '2px 2px 4px rgba(0, 0, 0, 0.5)';
    }
  }

  /**
   * Initialize responsive text handling
   */
  initResponsiveText(section) {
    const textElements = section.querySelectorAll('.index-hero__main-heading, .index-hero__sub-heading');
    
    // Adjust text size based on content length and viewport
    textElements.forEach(element => {
      this.adjustTextSize(element);
    });

    // Re-adjust on resize
    window.addEventListener('resize', this.debounce(() => {
      textElements.forEach(element => {
        this.adjustTextSize(element);
      });
    }, 250));
  }

  /**
   * Adjust text size based on content and viewport
   */
  adjustTextSize(element) {
    const textLength = element.textContent.length;
    const viewportWidth = window.innerWidth;
    
    if (this.isMobile()) {
      // Mobile text adjustments
      if (textLength > 30) {
        element.classList.add('text-sm');
      } else if (textLength > 20) {
        element.classList.add('text-base');
      }
    } else {
      // Desktop text adjustments
      if (textLength > 40) {
        element.style.fontSize = 'clamp(3rem, 5vw, 6rem)';
      }
    }
  }

  /**
   * Initialize accessibility features
   */
  initAccessibility(section) {
    // Add ARIA labels to interactive elements
    const buttons = section.querySelectorAll('a[class*="border-primary"]');
    buttons.forEach(button => {
      if (!button.getAttribute('aria-label')) {
        button.setAttribute('aria-label', button.textContent.trim());
      }
    });

    // Add focus management for video content
    const videos = section.querySelectorAll('video');
    videos.forEach(video => {
      video.setAttribute('tabindex', '0');
      video.setAttribute('aria-label', 'Hero video content');
      
      // Add keyboard controls
      video.addEventListener('keydown', (e) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          if (video.paused) {
            video.play();
          } else {
            video.pause();
          }
        }
      });
    });

    // Ensure proper heading hierarchy
    this.validateHeadingHierarchy(section);
  }

  /**
   * Validate and fix heading hierarchy
   */
  validateHeadingHierarchy(section) {
    const headings = section.querySelectorAll('h1, h2, h3, h4, h5, h6');
    let expectedLevel = 1;

    headings.forEach(heading => {
      const currentLevel = parseInt(heading.tagName.charAt(1));
      
      // Ensure proper heading sequence
      if (currentLevel > expectedLevel + 1) {
        console.warn(`Heading hierarchy issue: Found h${currentLevel} after h${expectedLevel - 1}`);
      }
      
      expectedLevel = Math.max(expectedLevel, currentLevel + 1);
    });
  }

  /**
   * Utility: Check if device is mobile
   */
  isMobile() {
    return window.innerWidth < 768;
  }

  /**
   * Utility: Debounce function
   */
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  /**
   * Performance optimization: Intersection Observer for animations
   */
  initPerformanceOptimizations() {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, observerOptions);

    // Observe all hero sections for performance
    this.sections.forEach(section => {
      observer.observe(section);
    });
  }

  /**
   * Error handling and logging
   */
  handleError(error, context) {
    console.error(`Index Hero Error in ${context}:`, error);
    
    // Optional: Send error to analytics
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: `Index Hero: ${context} - ${error.message}`,
        fatal: false
      });
    }
  }
}

/**
 * Initialize Index Hero functionality
 */
document.addEventListener('DOMContentLoaded', () => {
  try {
    new IndexHero();
  } catch (error) {
    console.error('Failed to initialize Index Hero:', error);
  }
});

/**
 * Export for potential external use
 */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = IndexHero;
}

/**
 * Performance monitoring
 */
if ('performance' in window) {
  window.addEventListener('load', () => {
    const loadTime = performance.now();
    console.log(`Index Hero loaded in ${loadTime.toFixed(2)}ms`);
  });
}

